import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
const ProgressPage = ({ API, userToken, isDarkMode, showNotification, setCurrentView }) => {
  const { t } = useTranslation();
  const [savedWords, setSavedWords] = useState([]);
  const [progressData, setProgressData] = useState({ puzzlePoints: 0, puzzlesCompleted: 0, badges: [], allBadges: [], earnedBadges: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBadgePanel, setShowBadgePanel] = useState(false);
  const [badgePanelTab, setBadgePanelTab] = useState('badges'); // 'badges' | 'help'
  const [helpText, setHelpText] = useState('');
  const [activeView, setActiveView] = useState('words'); // 'words' | 'puzzle'
  const [puzzleLevel, setPuzzleLevel] = useState(null); // 1, 2, 3
  const [puzzleState, setPuzzleState] = useState(null);
  const [dueReviews, setDueReviews] = useState([]);
  const [reviewCard, setReviewCard] = useState(null); // current review word
  const [reviewPhase, setReviewPhase] = useState('ask'); // 'ask' | 'quiz' | 'forgot' | 'result'
  const [quizOptions, setQuizOptions] = useState([]);
  const [quizAnswer, setQuizAnswer] = useState(null); // null | 'correct' | 'wrong'
  const [quizScript, setQuizScript] = useState('latin'); // 'latin' | 'arabic'
  const [wordListTab, setWordListTab] = useState('saved'); // 'saved' | 'bank' (mobile tabs)

  useEffect(() => {
    loadWordBank();
  }, []);

  const loadWordBank = async (skipReviews = false) => {
    try {
      const [wordsRes, progressRes] = await Promise.all([
        fetch(`${API}/learning/words`, { headers: { 'Authorization': `Bearer ${userToken}` } }),
        fetch(`${API}/progress`, { headers: { 'Authorization': `Bearer ${userToken}` } })
      ]);
      if (wordsRes.ok) {
        const wordsData = await wordsRes.json();
        setSavedWords(wordsData.words || []);
      }
      if (progressRes.ok) {
        const progData = await progressRes.json();
        setProgressData(progData);
      }
      // Load due reviews (skip after completing reviews to avoid race condition)
      if (!skipReviews) {
        try {
          const reviewsRes = await fetch(`${API}/learning/due-reviews`, {
            headers: { 'Authorization': `Bearer ${userToken}` }
          });
          if (reviewsRes.ok) {
            const rData = await reviewsRes.json();
            setDueReviews(rData.reviews || []);
          }
        } catch (e) { /* ignore */ }
      }
      // Load help text from settings
      try {
        const settingsRes = await fetch(`${API}/seo/public-settings`);
        if (settingsRes.ok) {
          const sData = await settingsRes.json();
          setHelpText(sData.wordBankHelpText || '');
        }
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Error loading word bank:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteWord = async (wordId) => {
    try {
      const response = await fetch(`${API}/learning/${wordId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        setSavedWords(prev => prev.filter(w => w.id !== wordId));
        showNotification(t('progress_word_removed'), 'success');
      }
    } catch (error) {
      console.error('Error deleting word:', error);
    }
  };

  // Split words by mastery level
  const savedList = savedWords.filter(w => (w.level || 0) < 5); // Not yet mastered
  const bankList = savedWords.filter(w => (w.level || 0) >= 5); // Mastered

  const matchesSearch = (w) => {
    const q = searchQuery.toLowerCase();
    const scripts = getWordScripts(w);
    return (w.sourceText || '').toLowerCase().includes(q) ||
      scripts.latin.toLowerCase().includes(q) ||
      scripts.arabic.toLowerCase().includes(q);
  };
  const filteredSaved = searchQuery ? savedList.filter(matchesSearch) : savedList;
  const filteredBank = searchQuery ? bankList.filter(matchesSearch) : bankList;

  // Find next badge — check both badges array AND earnedBadges
  const earnedBadgeIds = new Set([
    ...(progressData.badges || []),
    ...(progressData.earnedBadges || []).map(eb => eb.id)
  ]);
  const nextBadge = progressData.allBadges
    ?.filter(b => !earnedBadgeIds.has(b.id))
    ?.sort((a, b) => a.threshold - b.threshold)?.[0];

  const canPlay = savedWords.length >= 5;

  // ─── Puzzle Logic ────────────────────────────────────────────────────

  const startPuzzle = (level) => {
    if (!canPlay) return;
    const shuffled = [...savedWords].sort(() => Math.random() - 0.5).slice(0, 5);
    const words = shuffled.map(w => ({
      english: w.sourceText,
      darija: (getWordScripts(w).latin || '').toLowerCase().replace(/[^a-z0-9]/gi, '').trim(),
      id: w.id
    })).filter(w => w.darija.length > 0 && w.darija.length <= 15);

    if (words.length < 3) {
      showNotification(t('progress_not_enough_words'), 'error');
      return;
    }

    const currentWord = words[0];
    const tiles = generateTiles(currentWord.darija, level);

    setPuzzleLevel(level);
    setPuzzleState({
      words,
      currentIndex: 0,
      slots: Array(currentWord.darija.length).fill(null),
      tiles,
      score: 0,
      totalWords: words.length,
      completed: false,
      showResult: false,
      isCorrect: false,
      // Level 3 memory phase
      memoryPhase: level === 3,
      memoryTimer: level === 3 ? 10 : 0,
      peekUsed: false,
      peeking: false,
      newBadges: []
    });
    setActiveView('puzzle');

    if (level === 3) {
      startMemoryTimer();
    }
  };

  const generateTiles = (word, level) => {
    const letters = word.split('');
    let extraCount = 0;
    if (level === 2) extraCount = 10;
    if (level === 3) extraCount = Math.max(15, 20 - word.length);

    const extras = [];
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    for (let i = 0; i < extraCount; i++) {
      extras.push(alphabet[Math.floor(Math.random() * 26)]);
    }

    const allLetters = [...letters, ...extras];
    // Shuffle
    for (let i = allLetters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allLetters[i], allLetters[j]] = [allLetters[j], allLetters[i]];
    }

    return allLetters.map((letter, idx) => ({ letter, id: idx, used: false }));
  };

  const memoryTimerRef = React.useRef(null);

  const startMemoryTimer = () => {
    let seconds = 10;
    memoryTimerRef.current = setInterval(() => {
      seconds--;
      setPuzzleState(prev => {
        if (!prev) return prev;
        if (seconds <= 0) {
          clearInterval(memoryTimerRef.current);
          return { ...prev, memoryPhase: false, memoryTimer: 0 };
        }
        return { ...prev, memoryTimer: seconds };
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (memoryTimerRef.current) clearInterval(memoryTimerRef.current);
    };
  }, []);

  const handleTileTap = (tileId) => {
    if (!puzzleState || puzzleState.completed || puzzleState.showResult || puzzleState.memoryPhase) return;
    setPuzzleState(prev => {
      const tile = prev.tiles.find(t => t.id === tileId);
      if (!tile || tile.used) return prev;

      const nextSlotIdx = prev.slots.findIndex(s => s === null);
      if (nextSlotIdx === -1) return prev;

      const newSlots = [...prev.slots];
      newSlots[nextSlotIdx] = { letter: tile.letter, tileId: tile.id };

      const newTiles = prev.tiles.map(t => t.id === tileId ? { ...t, used: true } : t);

      // Check if all slots filled
      const allFilled = newSlots.every(s => s !== null);
      if (allFilled) {
        const answer = newSlots.map(s => s.letter).join('');
        const correct = answer === prev.words[prev.currentIndex].darija;
        const pointsPerLevel = { 1: 1, 2: 5, 3: (prev.peekUsed ? 0 : 10) };
        const earned = correct ? (pointsPerLevel[puzzleLevel] || 1) : 0;

        return {
          ...prev,
          slots: newSlots,
          tiles: newTiles,
          showResult: true,
          isCorrect: correct,
          score: prev.score + earned
        };
      }

      return { ...prev, slots: newSlots, tiles: newTiles };
    });
  };

  const handleSlotTap = (slotIdx) => {
    if (!puzzleState || puzzleState.completed || puzzleState.showResult || puzzleState.memoryPhase) return;
    setPuzzleState(prev => {
      const slot = prev.slots[slotIdx];
      if (!slot) return prev;

      const newSlots = [...prev.slots];
      newSlots[slotIdx] = null;

      const newTiles = prev.tiles.map(t => t.id === slot.tileId ? { ...t, used: false } : t);
      return { ...prev, slots: newSlots, tiles: newTiles };
    });
  };

  const clearAllSlots = () => {
    if (!puzzleState || puzzleState.completed || puzzleState.showResult) return;
    setPuzzleState(prev => {
      const newTiles = prev.tiles.map(t => ({ ...t, used: false }));
      const newSlots = Array(prev.words[prev.currentIndex].darija.length).fill(null);
      return { ...prev, slots: newSlots, tiles: newTiles };
    });
  };

  const nextWord = () => {
    setPuzzleState(prev => {
      const nextIdx = prev.currentIndex + 1;
      if (nextIdx >= prev.words.length) {
        // Puzzle complete — submit score
        submitPuzzleScore(prev.score, prev.words.length);
        return { ...prev, completed: true, showResult: false };
      }
      const nextW = prev.words[nextIdx];
      const tiles = generateTiles(nextW.darija, puzzleLevel);
      const newState = {
        ...prev,
        currentIndex: nextIdx,
        slots: Array(nextW.darija.length).fill(null),
        tiles,
        showResult: false,
        isCorrect: false,
        peekUsed: false,
        peeking: false,
        memoryPhase: puzzleLevel === 3,
        memoryTimer: puzzleLevel === 3 ? 10 : 0
      };
      if (puzzleLevel === 3) {
        setTimeout(() => startMemoryTimer(), 50);
      }
      return newState;
    });
  };

  const handlePeek = () => {
    if (!puzzleState || puzzleLevel !== 3) return;
    setPuzzleState(prev => ({ ...prev, peeking: true, peekUsed: true }));
    setTimeout(() => {
      setPuzzleState(prev => prev ? { ...prev, peeking: false } : prev);
    }, 2000);
  };

  const submitPuzzleScore = async (score, wordsCount) => {
    try {
      const formData = new FormData();
      formData.append('level', puzzleLevel);
      formData.append('words_completed', wordsCount);
      formData.append('points_earned', score);
      formData.append('token', userToken);

      const response = await fetch(`${API}/learning/puzzle-complete`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setPuzzleState(prev => prev ? {
          ...prev,
          newBadges: data.newBadges || []
        } : prev);
        // Refresh data
        loadWordBank();
      }
    } catch (error) {
      console.error('Error submitting puzzle score:', error);
    }
  };

  // ─── Review System (Spaced Repetition) ─────────────────────────────

  const startReview = () => {
    if (dueReviews.length === 0) return;
    setReviewCard(dueReviews[0]);
    setReviewPhase('ask');
    setQuizOptions([]);
    setQuizAnswer(null);
    setQuizScript('latin');
  };

  // Helper: detect if text is Arabic script
  const isArabicText = (text) => /[\u0600-\u06FF]/.test(text || '');

  // Helper: get latin and arabic from a word
  // Backend auto-fills latinScript from translations DB for old words.
  const getWordScripts = (w) => {
    const t = w.translatedText || '';
    const l = w.latinScript || '';
    const tIsArabic = isArabicText(t);
    const lIsLatin = l && !isArabicText(l);

    return {
      latin: lIsLatin ? l : (!tIsArabic ? t : ''),
      arabic: tIsArabic ? t : ''
    };
  };

  const generateQuizOptions = (correctWord) => {
    const correctScripts = getWordScripts(correctWord);
    // Get 3 random wrong options from other saved words — only use words with latin text
    const others = savedWords
      .filter(w => w.id !== correctWord.id)
      .map(w => getWordScripts(w))
      .filter(s => s.latin)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);

    // If not enough words for 3 wrong options, pad with fakes (both scripts)
    const fakes = [
      { latin: 'mashi', arabic: '\u0645\u0627\u0634\u064A' },
      { latin: 'wakha', arabic: '\u0648\u0627\u062E\u0627' },
      { latin: 'bzaf', arabic: '\u0628\u0632\u0627\u0641' },
      { latin: 'chwiya', arabic: '\u0634\u0648\u064A\u0629' },
      { latin: 'hadi', arabic: '\u0647\u0627\u062F\u064A' },
      { latin: 'daba', arabic: '\u062F\u0627\u0628\u0627' },
      { latin: 'labas', arabic: '\u0644\u0627\u0628\u0627\u0633' },
      { latin: 'mezyan', arabic: '\u0645\u0632\u064A\u0627\u0646' },
      { latin: 'khouya', arabic: '\u062E\u0648\u064A\u0627' },
      { latin: 'sahbi', arabic: '\u0635\u0627\u062D\u0628\u064A' }
    ];
    const shuffledFakes = fakes.sort(() => Math.random() - 0.5);
    let fakeIdx = 0;
    while (others.length < 3 && fakeIdx < shuffledFakes.length) {
      const fake = shuffledFakes[fakeIdx++];
      if (!others.find(o => o.latin === fake.latin) && fake.latin !== correctScripts.latin) {
        others.push(fake);
      }
    }

    // Combine and shuffle
    return [...others, correctScripts].sort(() => Math.random() - 0.5);
  };

  const handleRememberYes = () => {
    const options = generateQuizOptions(reviewCard);
    setQuizOptions(options);
    setReviewPhase('quiz');
    setQuizAnswer(null);
    setQuizScript('latin');
  };

  const handleRememberNo = () => {
    setReviewPhase('forgot');
  };

  const handleQuizPick = async (option) => {
    const correctScripts = getWordScripts(reviewCard);
    const isCorrect = option.latin === correctScripts.latin;
    setQuizAnswer(isCorrect ? 'correct' : 'wrong');

    // Submit result to backend
    try {
      const formData = new FormData();
      formData.append('learningId', reviewCard.id);
      formData.append('correct', isCorrect);
      formData.append('token', userToken);
      if (isCorrect) formData.append('master', 'true'); // Jump to level 5 (mastered)
      await fetch(`${API}/learning/review-result`, { method: 'POST', body: formData });
    } catch (e) { console.error('Error submitting review:', e); }

    if (isCorrect) {
      // Instantly move word to bank in local state (set level to 5)
      setSavedWords(prev => prev.map(w => w.id === reviewCard.id ? { ...w, level: 5 } : w));
      // Auto-advance to next word after 1.5s
      setTimeout(() => moveToNextReview(), 1500);
    }
    // If wrong: do NOT auto-advance. User sees "Wrong!" and must click "Start Over"
  };

  const handleRetryWord = () => {
    setReviewPhase('ask');
    setQuizOptions([]);
    setQuizAnswer(null);
    setQuizScript('latin');
  };

  const handleForgotNext = async () => {
    // "No" = word gets DELETED from saved
    try {
      await fetch(`${API}/learning/${reviewCard.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      // Remove from local state
      setSavedWords(prev => prev.filter(w => w.id !== reviewCard.id));
    } catch (e) { console.error('Error deleting word:', e); }

    moveToNextReview();
  };

  const moveToNextReview = () => {
    const remaining = dueReviews.filter(r => r.id !== reviewCard.id);
    setDueReviews(remaining);
    if (remaining.length > 0) {
      setReviewCard(remaining[0]);
      setReviewPhase('ask');
      setQuizOptions([]);
      setQuizAnswer(null);
      setQuizScript('latin');
    } else {
      setReviewCard(null);
      showNotification(t('progress_all_reviews_done'), 'success');
      loadWordBank(true); // skipReviews=true to avoid race condition reloading stale reviews
    }
  };

  // ─── Badge Panel Overlay ─────────────────────────────────────────────

  const defaultHelpContent = `How to Play

Save words from the translator, then test yourself with puzzles!

Easy — Spell the word from its own letters (1 pt/word)
Hard — Pick the right letters from extra distractors (5 pts/word)
Memory — Memorize the word for 10 seconds, then spell it from memory (10 pts/word)

Earn points to unlock badges! Check your progress in the Badges tab.

Tips:
- Tap a letter tile to place it in the next empty slot
- Tap a filled slot to remove that letter
- Use "Clear All" to start over
- In Memory mode, you can "Peek" but you'll get 0 points for that word`;

  const renderBadgePanel = () => {
    if (!showBadgePanel) return null;
    const earnedCount = progressData.earnedBadges?.length || 0;
    const totalCount = progressData.allBadges?.length || 0;

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => { setShowBadgePanel(false); setBadgePanelTab('badges'); }}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className={`relative w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header with tabs */}
          <div className={`sticky top-0 z-10 rounded-t-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-5 pb-0`}>
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {badgePanelTab === 'badges' ? `Badges (${earnedCount} / ${totalCount})` : 'How to Play'}
              </h2>
              <button
                onClick={() => { setShowBadgePanel(false); setBadgePanelTab('badges'); }}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Tabs */}
            <div className={`flex gap-1 px-5 pt-3 pb-0 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {[{ id: 'badges', label: 'Badges' }, { id: 'help', label: 'How to Play' }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setBadgePanelTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
                    badgePanelTab === tab.id
                      ? isDarkMode ? 'bg-gray-800 text-amber-400 border-b-2 border-amber-400' : 'bg-amber-50 text-amber-700 border-b-2 border-amber-500'
                      : isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          {badgePanelTab === 'badges' ? (
            <div className="p-5 space-y-3">
              {progressData.allBadges?.map((badge) => {
                const isEarned = progressData.badges?.includes(badge.id) || progressData.earnedBadges?.some(eb => eb.id === badge.id);
                return (
                  <div
                    key={badge.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                      isEarned
                        ? isDarkMode
                          ? 'bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border-yellow-500/60'
                          : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-400'
                        : isDarkMode
                        ? 'bg-gray-800/50 border-gray-700 opacity-50'
                        : 'bg-gray-50 border-gray-200 opacity-50'
                    }`}
                  >
                    <div className={`text-4xl flex-shrink-0 ${!isEarned ? 'grayscale' : ''}`}>
                      {badge.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{badge.name}</p>
                      <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{badge.description}</p>
                      {!isEarned && (
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                          {badge.threshold} points to unlock
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {isEarned ? (
                        <span className="text-green-500 text-xl">&#10003;</span>
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-5">
              <div className={`text-sm leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {helpText || defaultHelpContent}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Puzzle Game UI ──────────────────────────────────────────────────

  const renderPuzzle = () => {
    if (!puzzleState) return null;
    const currentWord = puzzleState.words[puzzleState.currentIndex];

    // Completion screen
    if (puzzleState.completed) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
          <div className={`w-full max-w-sm p-8 rounded-2xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
            <div className="text-5xl mb-4">&#127881;</div>
            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('progress_puzzle_complete')}</h2>
            <div className={`text-lg mb-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              +{puzzleState.score} points earned
            </div>
            <div className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total: {(progressData.puzzlePoints || 0) + puzzleState.score} points
            </div>

            {puzzleState.newBadges?.length > 0 && (
              <div className={`mb-6 p-4 rounded-xl ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-500/50' : 'bg-yellow-50 border border-yellow-300'}`}>
                <p className={`font-bold text-sm mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>NEW BADGE UNLOCKED!</p>
                {puzzleState.newBadges.map((b, i) => (
                  <div key={i} className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-2xl">{b.icon}</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{b.name}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => startPuzzle(puzzleLevel)}
                className="flex-1 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                {t('progress_play_again')}
              </button>
              <button
                onClick={() => { setActiveView('words'); setPuzzleState(null); setPuzzleLevel(null); }}
                className={`flex-1 py-3 rounded-xl font-bold transition-all ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Word Bank
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Memory phase (Level 3)
    if (puzzleState.memoryPhase) {
      const pct = (puzzleState.memoryTimer / 10) * 100;
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
          <div className="w-full max-w-sm mb-3">
            <button
              onClick={() => { setActiveView('words'); setPuzzleState(null); setPuzzleLevel(null); if (memoryTimerRef.current) clearInterval(memoryTimerRef.current); }}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          </div>
          <div className={`w-full max-w-sm p-8 rounded-2xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('progress_memorize_word')}</p>
            <p className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{currentWord.english}</p>
            <div className="text-4xl font-bold mb-6" style={{ color: isDarkMode ? '#f5c542' : '#b45309', letterSpacing: '6px' }}>
              {currentWord.darija}
            </div>
            <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className="h-full transition-all duration-1000"
                style={{
                  width: `${pct}%`,
                  background: pct > 60 ? '#22c55e' : pct > 30 ? '#eab308' : '#ef4444'
                }}
              />
            </div>
            <p className={`text-3xl font-bold mt-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{puzzleState.memoryTimer}s</p>
          </div>
        </div>
      );
    }

    // Active puzzle
    const usedTiles = puzzleState.tiles.filter(t => !t.used);
    const totalTiles = usedTiles.length + puzzleState.slots.filter(s => s !== null).length;

    return (
      <div className="flex flex-col items-center p-4 min-h-[60vh]">
        {/* Header */}
        <div className="w-full max-w-sm flex items-center justify-between mb-4">
          <button
            onClick={() => { setActiveView('words'); setPuzzleState(null); setPuzzleLevel(null); if (memoryTimerRef.current) clearInterval(memoryTimerRef.current); }}
            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="text-center">
            <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Level {puzzleLevel} &middot; Word {puzzleState.currentIndex + 1} of {puzzleState.totalWords}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-bold ${isDarkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
            {puzzleState.score} pts
          </div>
        </div>

        {/* English hint */}
        <div className={`w-full max-w-sm mb-6 p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200'}`}>
          <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('progress_translate_to_darija')}</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {currentWord.english}
            <span className={`text-xs font-normal ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({currentWord.darija.length} letters)</span>
          </p>
          {puzzleLevel === 3 && !puzzleState.peeking && (
            <button onClick={handlePeek} className={`mt-2 text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'} ${puzzleState.peekUsed ? 'opacity-50' : ''}`}>
              Peek (0 pts){puzzleState.peekUsed ? ' - used' : ''}
            </button>
          )}
          {puzzleState.peeking && (
            <p className="text-lg font-bold mt-2" style={{ color: '#f59e0b', letterSpacing: '4px' }}>{currentWord.darija}</p>
          )}
        </div>

        {/* Answer slots */}
        <div className="w-full max-w-sm mb-6">
          <p className={`text-xs font-medium mb-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('progress_your_answer')}</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {puzzleState.slots.map((slot, idx) => (
              <button
                key={idx}
                onClick={() => handleSlotTap(idx)}
                className={`w-11 h-12 rounded-lg flex items-center justify-center text-lg font-bold border-2 transition-all ${
                  slot
                    ? puzzleState.showResult
                      ? puzzleState.isCorrect
                        ? 'bg-green-500 border-green-400 text-white'
                        : 'bg-red-500 border-red-400 text-white animate-pulse'
                      : isDarkMode
                      ? 'bg-amber-800/60 border-amber-500 text-amber-100'
                      : 'bg-amber-100 border-amber-400 text-amber-900'
                    : isDarkMode
                    ? 'bg-gray-800 border-gray-600'
                    : 'bg-white border-gray-300'
                }`}
                style={slot ? { boxShadow: '0 3px 6px rgba(0,0,0,0.15)' } : {}}
              >
                {slot ? slot.letter.toUpperCase() : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Result message */}
        {puzzleState.showResult && (
          <div className={`w-full max-w-sm mb-4 p-3 rounded-xl text-center font-bold ${
            puzzleState.isCorrect
              ? isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
              : isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
          }`}>
            {puzzleState.isCorrect ? 'Correct!' : `Wrong! Answer: ${currentWord.darija}`}
            <button
              onClick={nextWord}
              className="ml-3 px-4 py-1 rounded-lg text-sm text-white bg-gradient-to-r from-amber-500 to-orange-500"
            >
              Next
            </button>
          </div>
        )}

        {/* Letter tiles — circular/grid layout */}
        <div className="w-full max-w-sm">
          <p className={`text-xs font-medium mb-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('progress_pick_letters')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {puzzleState.tiles.map((tile) => (
              <button
                key={tile.id}
                onClick={() => handleTileTap(tile.id)}
                disabled={tile.used || puzzleState.showResult}
                className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold transition-all ${
                  tile.used
                    ? 'opacity-0 pointer-events-none'
                    : isDarkMode
                    ? 'bg-gradient-to-br from-amber-700 to-amber-900 text-amber-100 border border-amber-600 hover:from-amber-600 hover:to-amber-800 active:scale-90'
                    : 'bg-gradient-to-br from-amber-400 to-orange-400 text-white border border-amber-300 hover:from-amber-500 hover:to-orange-500 active:scale-90'
                }`}
                style={!tile.used ? { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' } : {}}
              >
                {tile.letter.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Clear button */}
        {!puzzleState.showResult && (
          <button
            onClick={clearAllSlots}
            className={`mt-4 px-5 py-2 rounded-xl text-sm font-medium transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
          >
            Clear All
          </button>
        )}
      </div>
    );
  };

  // ─── Main Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className={`animate-spin w-8 h-8 border-3 rounded-full ${isDarkMode ? 'border-amber-500 border-t-transparent' : 'border-amber-600 border-t-transparent'}`} style={{ borderWidth: '3px' }} />
      </div>
    );
  }

  // Puzzle view
  if (activeView === 'puzzle' && puzzleState) {
    return (
      <div className="max-w-4xl mx-auto">
        {renderPuzzle()}
        {renderBadgePanel()}
      </div>
    );
  }

  // Word Bank main view
  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {setCurrentView && (
            <button onClick={() => setCurrentView('translator')}
              className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('word_bank')}</h1>
            <div className={`flex items-center gap-3 text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>{savedList.length} saved</span>
              <span>&middot;</span>
              <span>{bankList.length} in bank</span>
              <span>&middot;</span>
              <span className="text-amber-500 font-medium">{progressData.puzzlePoints || 0} pts</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowBadgePanel(true)}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}
          title={t('dashboard_badges')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Progress bar to next badge */}
      {nextBadge && (
        <div className={`mb-5 p-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-amber-50 to-yellow-50'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Next: {nextBadge.icon} {nextBadge.name}
            </span>
            <span className={`text-xs font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              {progressData.puzzlePoints || 0} / {nextBadge.threshold} pts
            </span>
          </div>
          <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500 rounded-full"
              style={{ width: `${Math.min(((progressData.puzzlePoints || 0) / nextBadge.threshold) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Puzzle Launcher */}
      <div className="mb-6">
        <h2 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('progress_word_puzzle')}</h2>
        <div className="grid grid-cols-3 gap-3">
          {[
            { level: 1, label: t('progress_easy'), color: 'green', emoji: '', pts: '1 pt/word', desc: t('progress_easy_desc') },
            { level: 2, label: t('progress_hard'), color: 'yellow', emoji: '', pts: '5 pts/word', desc: t('progress_hard_desc') },
            { level: 3, label: t('progress_memory'), color: 'red', emoji: '', pts: '10 pts/word', desc: t('progress_memory_desc') }
          ].map(({ level, label, color, emoji, pts, desc }) => (
            <button
              key={level}
              onClick={() => startPuzzle(level)}
              disabled={!canPlay}
              className={`relative p-4 rounded-xl text-center transition-all ${
                !canPlay
                  ? isDarkMode ? 'bg-gray-800/50 opacity-40' : 'bg-gray-100 opacity-40'
                  : isDarkMode
                  ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-amber-500/50'
                  : 'bg-white hover:shadow-md border border-gray-200 hover:border-amber-400'
              }`}
            >
              <div className={`text-2xl mb-1 ${
                color === 'green' ? 'text-green-500' : color === 'yellow' ? 'text-yellow-500' : 'text-red-500'
              }`}>
                {level === 1 ? '\u{1F7E2}' : level === 2 ? '\u{1F7E1}' : '\u{1F534}'}
              </div>
              <p className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{label}</p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{desc}</p>
              <p className={`text-xs mt-1 font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{pts}</p>
            </button>
          ))}
        </div>
        {!canPlay && (
          <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {t('pp_save_more_to_unlock').replace('{n}', 5 - savedWords.length).replace('{wordOrWords}', '').replace(/  +/g, ' ').trim()}
          </p>
        )}
      </div>

      {/* Review Card */}
      {reviewCard ? (
        <div className={`mb-6 p-5 rounded-xl border-2 ${isDarkMode ? 'bg-gray-800 border-blue-500/40' : 'bg-blue-50 border-blue-300'}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
              {t('pp_review_remaining').replace('{n}', dueReviews.length)}
            </h2>
            <button
              onClick={() => { setReviewCard(null); setReviewPhase('ask'); setQuizAnswer(null); }}
              className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'}`}
            >
              {t('pp_skip')}
            </button>
          </div>

          {/* English word always shown */}
          <p className={`text-xl font-bold text-center mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {reviewCard.sourceText}
          </p>

          {/* Phase: Ask - Do you remember? */}
          {reviewPhase === 'ask' && (
            <div>
              <p className={`text-xs text-center mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('pp_remember_translation')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleRememberNo}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {t('pp_no')}
                </button>
                <button
                  onClick={handleRememberYes}
                  className="flex-1 py-3 rounded-xl font-bold text-sm bg-blue-500 text-white hover:bg-blue-600 transition-all"
                >
                  {t('pp_yes_remember')}
                </button>
              </div>
              <p className={`text-xs text-center mt-3 ${isDarkMode ? 'text-red-400/60' : 'text-red-400'}`}>
                {t('pp_click_no_hint')}
              </p>
            </div>
          )}

          {/* Phase: Quiz - Pick the right answer */}
          {reviewPhase === 'quiz' && (
            <div>
              <p className={`text-xs text-center mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('pp_pick_correct')}
              </p>
              {(() => { const correctScripts = getWordScripts(reviewCard);
              const allHaveLatin = quizOptions.every(o => o.latin);
              const allHaveArabic = quizOptions.every(o => o.arabic && o.arabic !== o.latin);
              const canToggle = allHaveLatin && allHaveArabic;
              // If can't toggle: show whichever script ALL options have
              const activeScript = canToggle ? quizScript : (allHaveLatin ? 'latin' : 'arabic');
              return (
              <>
              <div className="grid grid-cols-2 gap-2">
                {quizOptions.map((option, idx) => {
                  const isCorrectOption = option.latin === correctScripts.latin;
                  const displayText = activeScript === 'arabic' ? option.arabic : option.latin;
                  let btnClass = isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600 border-gray-600' : 'bg-white text-gray-800 hover:bg-gray-50 border-gray-300';
                  if (quizAnswer) {
                    if (isCorrectOption) btnClass = 'bg-green-500 text-white border-green-400';
                    else if (quizAnswer === 'wrong' && !isCorrectOption) btnClass = isDarkMode ? 'bg-gray-700/50 text-gray-500 border-gray-700' : 'bg-gray-100 text-gray-400 border-gray-200';
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => !quizAnswer && handleQuizPick(option)}
                      disabled={!!quizAnswer}
                      className={`py-3 px-3 rounded-xl text-sm font-medium border transition-all ${btnClass}`}
                      dir={activeScript === 'arabic' ? 'rtl' : 'ltr'}
                    >
                      {displayText}
                    </button>
                  );
                })}
              </div>
              {/* Latin/Arabic toggle — only show if all options have Arabic */}
              {!quizAnswer && canToggle && (
                <div className="flex justify-center mt-3">
                  <div className={`inline-flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <button
                      onClick={() => setQuizScript('latin')}
                      className={`text-xs px-3 py-1.5 font-medium transition-all ${
                        activeScript === 'latin'
                          ? 'bg-blue-500 text-white'
                          : (isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500')
                      }`}
                    >
                      {t('pp_latin')}
                    </button>
                    <button
                      onClick={() => setQuizScript('arabic')}
                      className={`text-xs px-3 py-1.5 font-medium transition-all ${
                        activeScript === 'arabic'
                          ? 'bg-blue-500 text-white'
                          : (isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500')
                      }`}
                    >
                      {'\u0639\u0631\u0628\u064A'}
                    </button>
                  </div>
                </div>
              )}
              {quizAnswer === 'correct' && (
                <p className="text-center text-sm font-bold mt-3 text-green-500">
                  {t('pp_correct_added')}
                </p>
              )}
              {quizAnswer === 'wrong' && (
                <div className="mt-3">
                  <p className="text-center text-sm font-bold text-red-500">
                    {t('pp_wrong_answer_is').replace('{x}', correctScripts.latin)}
                  </p>
                  <button
                    onClick={handleRetryWord}
                    className="w-full mt-3 py-3 rounded-xl font-bold text-sm bg-red-500 text-white hover:bg-red-600 transition-all"
                  >
                    {t('pp_start_over')}
                  </button>
                </div>
              )}
              </>
              ); })()}
            </div>
          )}

          {/* Phase: Forgot - Show answer, word will be deleted */}
          {reviewPhase === 'forgot' && (() => { const scripts = getWordScripts(reviewCard); return (
            <div>
              <p className={`text-xs text-center mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('pp_answer_was')}
              </p>
              <p className="text-center text-2xl font-bold mb-1" style={{ color: isDarkMode ? '#f5c542' : '#b45309', letterSpacing: '3px' }}>
                {scripts.latin}
              </p>
              {scripts.arabic && scripts.arabic !== scripts.latin && (
                <p className="text-center text-lg font-medium mb-2" dir="rtl" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                  {scripts.arabic}
                </p>
              )}
              <p className={`text-xs text-center mb-4 ${isDarkMode ? 'text-red-400/60' : 'text-red-400'}`}>
                {t('pp_will_be_removed')}
              </p>
              <button
                onClick={handleForgotNext}
                className="w-full py-3 rounded-xl font-bold text-sm bg-blue-500 text-white hover:bg-blue-600 transition-all"
              >
                {t('pp_next')}
              </button>
            </div>
          ); })()}
        </div>
      ) : dueReviews.length > 0 ? (
        <div className="mb-6">
          <button
            onClick={startReview}
            className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
              isDarkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-500/40 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100'
            }`}
          >
            <span>{'\u{1F4AC}'}</span> {t('pp_words_to_review').replace('{n}', dueReviews.length).replace('{wordOrWords}', '').replace(/  +/g, ' ').trim()}
          </button>
        </div>
      ) : null}

      {/* Search */}
      <div className="mb-4">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={t('progress_search_words')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={`flex-1 bg-transparent outline-none text-sm ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
          />
        </div>
      </div>

      {/* ── Mobile Tabs (visible on small screens only) ── */}
      <div className="flex md:hidden mb-4 rounded-xl overflow-hidden border" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
        <button
          onClick={() => setWordListTab('saved')}
          className={`flex-1 py-2.5 text-sm font-bold transition-all ${
            wordListTab === 'saved'
              ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white')
              : (isDarkMode ? 'bg-gray-800/50 text-gray-500' : 'bg-gray-50 text-gray-500')
          }`}
        >
          {t('pp_tab_saved')} ({filteredSaved.length})
        </button>
        <button
          onClick={() => setWordListTab('bank')}
          className={`flex-1 py-2.5 text-sm font-bold transition-all ${
            wordListTab === 'bank'
              ? (isDarkMode ? 'bg-green-800 text-green-200' : 'bg-green-600 text-white')
              : (isDarkMode ? 'bg-gray-800/50 text-gray-500' : 'bg-gray-50 text-gray-500')
          }`}
        >
          {t('pp_tab_word_bank')} ({filteredBank.length})
        </button>
      </div>

      {/* ── Desktop: side by side | Mobile: tab content ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">

        {/* ── SAVED WORDS Column ── */}
        <div className={`space-y-2 ${wordListTab !== 'saved' ? 'hidden md:block' : ''}`}>
          <div className="mb-1">
            <h2 className={`text-sm font-bold text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('pp_saved_words_h')} ({filteredSaved.length})
            </h2>
            <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('pp_saved_words_sub')}
            </p>
          </div>
          {filteredSaved.length === 0 ? (
            <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className="text-3xl mb-2">{savedList.length === 0 ? '\u{1F4DD}' : '\u{1F50D}'}</div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {savedList.length === 0
                  ? t('pp_translate_save_to_start')
                  : t('pp_no_words_match')}
              </p>
            </div>
          ) : (
            filteredSaved.map(word => (
              <div
                key={word.id}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${isDarkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white border border-gray-100 hover:shadow-sm'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.sourceText}</span>
                    <span className={`${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>&rarr;</span>
                    <span className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{getWordScripts(word).latin || word.translatedText}</span>
                  </div>
                  {word.createdAt && (
                    <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      {new Date(word.createdAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => deleteWord(word.id)}
                  className={`p-1.5 rounded-lg ml-2 ${isDarkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

        {/* ── WORD BANK Column ── */}
        <div className={`space-y-2 ${wordListTab !== 'bank' ? 'hidden md:block' : ''} ${wordListTab === 'bank' ? '' : 'md:mt-0 mt-0'}`}>
          <div className="mb-1">
            <h2 className={`text-sm font-bold text-center ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
              {t('pp_word_bank_h')} ({filteredBank.length})
            </h2>
            <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('pp_word_bank_sub')}
            </p>
          </div>
          {filteredBank.length === 0 ? (
            <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className="text-3xl mb-2">{'\u{1F3E6}'}</div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('pp_review_to_build')}
              </p>
            </div>
          ) : (
            filteredBank.map(word => (
              <div
                key={word.id}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${isDarkMode ? 'bg-green-900/20 border border-green-800/30 hover:bg-green-900/30' : 'bg-green-50 border border-green-200 hover:shadow-sm'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-green-500 text-xs">&#10003;</span>
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.sourceText}</span>
                    <span className={`${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>&rarr;</span>
                    <span className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{getWordScripts(word).latin || word.translatedText}</span>
                  </div>
                </div>
                <button
                  onClick={() => deleteWord(word.id)}
                  className={`p-1.5 rounded-lg ml-2 ${isDarkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

      </div>

      {renderBadgePanel()}
    </div>
  );
};


// Conversation Modal Component

export default ProgressPage;
